import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SpinnerGap, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/**
 * Magic-login-mottagare.
 * När en anställd klickar på länken från admin-mailet landar de här.
 * Vi byter token mot en session via POST /auth/magic-login och skickar
 * dem direkt vidare till stämpelklockan — inget lösenord behövs.
 */
export default function LoginLink() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("Länken saknar giltig token.");
      return;
    }
    (async () => {
      try {
        const r = await api.post("/auth/magic-login", { token });
        if (r.data?.access_token || r.data?.refresh_token) {
          tokenStore.set(r.data.access_token, r.data.refresh_token);
        }
        if (typeof checkAuth === "function") {
          await checkAuth();
        }
        toast.success("Du är inloggad!");
        // Skicka vidare — stämpelklockan är default för anställda.
        navigate("/", { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail || "Länken är ogiltig eller har gått ut.";
        setErrorMsg(detail);
        setStatus("error");
      }
    })();
  }, [params, navigate, checkAuth]);

  return (
    <div
      data-testid="magic-login-page"
      className="min-h-screen flex items-center justify-center bg-app px-4"
    >
      <div className="tk-card max-w-md w-full text-center py-10 px-8">
        {status === "loading" && (
          <>
            <SpinnerGap
              size={40}
              className="animate-spin mx-auto text-brand-primary"
              data-testid="magic-login-spinner"
            />
            <h1 className="font-heading text-2xl mt-4">Loggar in dig…</h1>
            <p className="text-sm text-text-muted mt-2">
              Vi verifierar din länk och startar en session.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <WarningCircle size={40} className="mx-auto text-red-500" />
            <h1 className="font-heading text-2xl mt-4">Kunde inte logga in</h1>
            <p
              className="text-sm text-text-muted mt-2"
              data-testid="magic-login-error"
            >
              {errorMsg}
            </p>
            <p className="text-sm text-text-muted mt-4">
              Be din administratör skicka en ny länk, eller logga in med
              e-post och lösenord.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="tk-btn-primary mt-6"
              data-testid="magic-login-goto-login"
            >
              Till inloggningen
            </button>
          </>
        )}
      </div>
    </div>
  );
}
